import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { Locale, Translation } from 'locale'
import { DialogWindow } from 'view/components/panels/DialogWindow'
import {
  Collection,
  CollectionActions,
  CollectionModifyOrdersArgs,
  CollectionType,
  EntityType,
  NegotiationFieldMeta,
  Order,
  OrderActions,
  OrderCreateArgs,
  RfqType,
  UserPreference
} from 'api'
import { AssignToCollectionPanel } from 'view/shared/collections/AssignToCollectionPanel'
import { FundComboboxField, InstrumentComboboxField } from 'view/shared/entity'
import { DialogField, DialogRow, NumericSpinnerField } from 'view/components/inputs'
import { Groupbox } from 'view/components/panels'
import { createSelectedCollectionIdSelector } from 'selectors/selectionSelectors'
import { createFundListByInstrumentSelector } from 'selectors/tradingEntitySelectors'
import { GridId } from 'store/grid'
import {
  createInvalidValueValidationIssue,
  generateUploadRowIndex,
  getMaxCollectionSize,
  getUploadRowIndexPrefix,
  MAX_COLLECTION_SIZE,
  newEmptyOrderAttributes
} from 'model'
import { createOrdersWithCollectionIdGetterSelector } from 'selectors/orderSelectors'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { NotificationActions, NotificationType } from 'store/notification'
import { ModalConsumerProps, withModal } from '../../shared/modal'
import { availableCollectionsSelector } from 'selectors/collectionSelectors'
import { confirmAddMatchingToExisting } from '../../shared/collections/utils'
import { Decimal } from 'utils'

type ShouldModifyResponse = {
  cancelled: boolean
  rfqType?: RfqType
}

export interface AddAssetDialogProps extends ModalConsumerProps {
  initialCollectionId: string | null
  allFundIds: string[]
  onClose?: () => unknown
  onCreateOrder: (args: OrderCreateArgs) => unknown
  getOrdersInCollection: (collectionId: string) => Order[]
  getCollection: (collectionId: string) => Collection | null
  userPreference: UserPreference
  onModifyCollectionOrders: (args: CollectionModifyOrdersArgs) => unknown
  availableCollections: Collection[]
}

interface AddAssetDialogState {
  fundId: string | null
  instrumentId: string | null
  collectionId: string | null
  newCollectionName: string | null
  lineCount: number
  newCollectionType: CollectionType | null
}

const mapStateToProps = createStructuredSelector({
  initialCollectionId: createSelectedCollectionIdSelector(GridId.StagingGrid),
  allFundIds: createFundListByInstrumentSelector(),
  getOrdersInCollection: createOrdersWithCollectionIdGetterSelector,
  getCollection: createEntityGetterSelector(EntityType.COLLECTION),
  userPreference: createUserPreferenceSelector(),
  availableCollections: availableCollectionsSelector
})

const mapDispatchToProps = {
  onCreateOrder: OrderActions.create,
  onShowDialog: NotificationActions.showDialog,
  onModifyCollectionOrders: CollectionActions.modifyOrders
}

export class AddAssetDialogComponent extends React.Component<AddAssetDialogProps, AddAssetDialogState> {
  constructor(props: AddAssetDialogProps) {
    super(props)
    this.state = {
      fundId: props.allFundIds.length === 1 ? props.allFundIds[0] : null,
      instrumentId: null,
      newCollectionName: null,
      collectionId: props.initialCollectionId,
      lineCount: 1,
      newCollectionType: null
    }
  }

  render() {
    const { onClose } = this.props
    const { fundId, instrumentId, collectionId, newCollectionName, lineCount, newCollectionType } = this.state
    const assetDetailsSet = fundId && instrumentId

    const maxLineCount = this.getMaxLineCount()
    const lineCountValidationIssues =
      maxLineCount !== null && lineCount > maxLineCount
        ? [
            createInvalidValueValidationIssue(
              'lineCount',
              Locale.translate(Translation.SC_MAX_COLLECTION_SIZE_TOOLTIP, {
                remainingLegs: maxLineCount,
                maxLegs: MAX_COLLECTION_SIZE
              })
            )
          ]
        : []

    return (
      <DialogWindow
        title={Translation.TC_ADD_NEW_ASSETS}
        confirmButtonEnabled={!!assetDetailsSet && lineCountValidationIssues.length === 0 && newCollectionName?.length !== 0}
        onConfirm={this.submitUpdate}
        confirmLabel={Translation.TC_BUTTON_OK}
        onClose={onClose}>
        <Groupbox title={Locale.translate(Translation.TC_ENTER_ASSET_DETAILS)}>
          <DialogRow>
            <DialogField>
              <FundComboboxField
                label={Locale.translate(Translation.TC_FUND_LABEL)}
                placeholder={Locale.translate(Translation.SC_FUND_PLACEHOLDER)}
                value={fundId}
                instrumentId={instrumentId}
                onUpdate={this.updateFund}
                dataTestId={'AddAssetDialog-FundComboboxField-Fundlabel'}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <InstrumentComboboxField
                label={Locale.translate(Translation.TC_INSTRUMENT_LABEL)}
                placeholder={Locale.translate(Translation.SC_INSTRUMENT_PLACEHOLDER)}
                value={instrumentId}
                fundId={fundId}
                isVirtualized={true}
                onUpdate={this.updateInstrument}
                dataTestId={'AddAssetDialog-InstrumentComboboxField-InstrumentLabel'}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <NumericSpinnerField
                dataTestId={'AddAssetDialog-NumericSpinnerField-Number'}
                fieldName="lineCount"
                label={Locale.translate(Translation.TC_NUMBER)}
                value={lineCount}
                onUpdate={this.updateLineCount}
                validationIssues={lineCountValidationIssues}
                step={1}
                min={1}
                max={maxLineCount ?? undefined}
              />
            </DialogField>
          </DialogRow>
        </Groupbox>
        <AssignToCollectionPanel
          orderIds={[]}
          collectionId={collectionId}
          newCollectionName={newCollectionName}
          newCollectionType={newCollectionType}
          disabled={!assetDetailsSet}
          onUpdate={this.updateCollection}
        />
      </DialogWindow>
    )
  }

  private updateFund = (fundId: string | null) => {
    this.setState({ fundId })
  }

  private updateInstrument = (instrumentId: string | null) => {
    this.setState({ instrumentId })
  }

  private updateCollection = (collectionId: string | null, newCollectionName: string | null, newCollectionType: CollectionType | null) => {
    this.setState({ collectionId, newCollectionName, newCollectionType })
  }

  private updateLineCount = (lineCount: number) => {
    this.setState({ lineCount })
  }

  private getMaxLineCount = (): number | null => {
    const { getOrdersInCollection } = this.props
    const { collectionId, newCollectionName, newCollectionType } = this.state

    const maxSize = getMaxCollectionSize(newCollectionType)

    if (collectionId !== null) {
      return Decimal.subtract(maxSize, getOrdersInCollection(collectionId).length)
    } else if (newCollectionName !== null) {
      return maxSize
    }

    return null
  }

  private getShouldModifyCollectionRfqType = async (): Promise<ShouldModifyResponse> => {
    const { getCollection, getOrdersInCollection, showDialog, userPreference } = this.props
    const { collectionId } = this.state

    // Its being added to an existing collection
    if (collectionId) {
      const collection = getCollection(collectionId)

      if (collection) {
        const ordersInCollection = getOrdersInCollection(collection.id)

        if (
          // Only AON collections have requirement for same RFQ type
          collection?.type === CollectionType.ALL_OR_NOTHING &&
          // Collection has legs
          ordersInCollection.length > 0 &&
          // We can assume that all orders in an AON collection are of the same RFQ type so pick the first
          ordersInCollection[0].rfqType !== userPreference.defaultRfqType
        ) {
          const dialogArgs = {
            type: NotificationType.Confirm,
            title: Translation.SC_CANNOT_MIX_RFQ_TYPE,
            message: Translation.SC_CANNOT_MIX_RFQ_TYPE_MESSAGE,
            messageArgs: {
              packageName: collection.name,
              orderRFQTye: userPreference.defaultRfqType,
              packageRfqType: ordersInCollection[0].rfqType
            },
            buttons: [Translation.TC_BUTTON_CANCEL, Translation.TC_RFQ_TYPE_INDICATIVE, Translation.TC_RFQ_TYPE_FIRM],
            cancelButton: Translation.TC_BUTTON_CANCEL,
            defaultButton:
              userPreference.defaultRfqType === RfqType.FIRM ? Translation.TC_RFQ_TYPE_FIRM : Translation.TC_RFQ_TYPE_INDICATIVE
          }

          const dialogResult = await showDialog(dialogArgs)

          switch (dialogResult) {
            case Translation.TC_RFQ_TYPE_FIRM:
              return { rfqType: RfqType.FIRM, cancelled: false }
            case Translation.TC_RFQ_TYPE_INDICATIVE:
              return { rfqType: RfqType.INDICATIVE, cancelled: false }
            default:
              return { cancelled: true }
          }
        }
      }
    }
    return { cancelled: false }
  }

  private submitUpdate = async () => {
    const { onModifyCollectionOrders, showDialog, availableCollections } = this.props
    const { fundId, instrumentId, collectionId, newCollectionName, lineCount, newCollectionType } = this.state

    let shouldModifyCollection: ShouldModifyResponse

    if (collectionId) {
      shouldModifyCollection = await this.getShouldModifyCollectionRfqType()

      if (shouldModifyCollection.cancelled) {
        return
      }

      if (shouldModifyCollection && shouldModifyCollection.rfqType) {
        onModifyCollectionOrders({
          collectionId,
          updatedValues: {
            [NegotiationFieldMeta.RFQ_TYPE]: shouldModifyCollection.rfqType
          }
        })
      }
    }

    const addMatchingToExisting = await confirmAddMatchingToExisting(showDialog, availableCollections, newCollectionName, newCollectionType)

    if (addMatchingToExisting.cancelled) {
      return
    }

    const uploadRowIndexPrefix = getUploadRowIndexPrefix()

    const orderAttributes = Array(lineCount)
      .fill({})
      .map((_newOrder, idx) =>
        newEmptyOrderAttributes(
          {
            collateralId: instrumentId,
            basketId: instrumentId,
            fundId: fundId,
            ...(shouldModifyCollection && { rfqType: shouldModifyCollection.rfqType }),
            uploadRowIndex: generateUploadRowIndex(uploadRowIndexPrefix, idx + 1)
          },
          addMatchingToExisting && addMatchingToExisting.matchedCollectionId ? addMatchingToExisting.matchedCollectionId : collectionId,
          addMatchingToExisting && addMatchingToExisting.matchedCollectionId ? null : newCollectionName,
          newCollectionName ? newCollectionType : null
        )
      )

    this.props.onCreateOrder({
      orderAttributes,
      deleteCurrentOrdersInStaging: false,
      spreadsheetContent: null,
      ignoreAxeId: null,
      performUserValidations: false
    })

    if (this.props.onClose) {
      this.props.onClose()
    }
  }
}

export const AddAssetDialog = withModal(connect(mapStateToProps, mapDispatchToProps)(AddAssetDialogComponent))
